'use client'

import * as React from 'react'
import * as Popover from 'primitives/popover'
import * as Dialog from 'primitives/dialog'
import { useTranslations } from 'next-intl'
import toast from 'react-hot-toast'
import { useIsLoggedInQuery } from 'auth/hooks'

import { Button } from '@repo/design-system-cf/components/button'
import {
  IconMoreHoriz,
  IconNotificationsOff,
  IconNotifications,
  IconClose,
} from '@repo/design-system-cf/components/icons'
import { useClubhousesDiscussionNotificationUpdateMutation } from '@web/gen/clubhouses'
import { TipButton } from '../../(social)/discussions/[topicSlug]/[discussionSlug]/_components/tip-button-v2'
import { ShareButton } from '../../(social)/discussions/[topicSlug]/[discussionSlug]/_components/share-button'

interface Props {
  isSubscribedToAllNotifications: boolean
  discussionId: string
  discussionSlug: string
  detailsHref: string
}

export function DiscussionPopoverMenu({
  isSubscribedToAllNotifications,
  discussionId,
  discussionSlug,
  detailsHref,
}: Props) {
  const t = useTranslations('pages.clubhouse.discussions')
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const clubhousesDiscussionNotificationUpdateMutation =
    useClubhousesDiscussionNotificationUpdateMutation()
  const [isSubscribed, setIsSubscribed] = React.useState(isSubscribedToAllNotifications)
  const [isOpen, setIsOpen] = React.useState(false)

  const prevent = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(false)
  }

  const handleToggleSubscription = (e: React.MouseEvent) => {
    prevent(e)
    const next = !isSubscribed
    setIsSubscribed(next)

    clubhousesDiscussionNotificationUpdateMutation.mutate(
      { input: { discussionId: Number(discussionId), subscribe: next } },
      {
        onSuccess: () => {
          toast.success(next ? t('turn_on_notifications') : t('turn_off_email_notifications'), {
            duration: 5000,
          })
          setIsOpen(false)
        },
        onError: () => {
          setIsSubscribed(!next)
          toast.error(t('something_went_wrong'))
          setIsOpen(false)
        },
      },
    )
  }

  const renderMenuItems = () => (
    <>
      {isLoggedIn ? (
        <div onClick={prevent}>
          <TipButton withText discussionId={discussionId} discussionSlug={discussionSlug} />
        </div>
      ) : null}

      {isLoggedIn ? (
        <div
          onClick={handleToggleSubscription}
          className="pointer-events-auto flex items-center gap-2 p-2 w-full rounded hover:bg-cf-grey-100 cursor-pointer"
        >
          {isSubscribed ? (
            <IconNotificationsOff className="size-5" />
          ) : (
            <IconNotifications className="size-5" />
          )}
          <p className="text-14 font-medium">
            {isSubscribed ? t('turn_off_notifications') : t('turn_on_notifications')}
          </p>
        </div>
      ) : null}

      <div onClick={prevent}>
        <ShareButton withText detailsHref={detailsHref} />
      </div>
    </>
  )

  return (
    <>
      <Popover.Root>
        <div
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <Popover.Trigger asChild>
            <Button
              className="pointer-events-auto hidden sm:block text-cf-purple-900"
              variant="ghost"
              size="small"
              iconRight={<IconMoreHoriz />}
            />
          </Popover.Trigger>
        </div>

        <Popover.Content
          onClick={e => {
            e.stopPropagation()
          }}
          side="bottom"
          align="end"
          sideOffset={8}
          className="max-w-96 rounded-2 bg-cf-white p-1 shadow-[0px_2px_6px_2px_rgba(0,0,0,0.125)] text-cf-grey-800"
        >
          {renderMenuItems()}
        </Popover.Content>
      </Popover.Root>

      <Dialog.Root open={isOpen} onOpenChange={setIsOpen} defaultOpen={false}>
        <Dialog.Trigger asChild>
          <Button
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              setIsOpen(true)
            }}
            className="pointer-events-auto sm:hidden text-cf-purple-900"
            variant="ghost"
            size="small"
            iconRight={<IconMoreHoriz />}
          />
        </Dialog.Trigger>

        <Dialog.Portal>
          <Dialog.Body zIndex={1102} overlayZIndex={1102} className="bg-[#13131500]">
            <div className="absolute bottom-0 w-full min-h-48 rounded-tl-[20px] rounded-tr-[20px] bg-cf-white">
              <div className="flex justify-between items-center pt-3 pb-4 pl-5 pr-2">
                <p className="text-16 font-semibold">Options</p>
                <Button
                  onClick={() => setIsOpen(false)}
                  className="pointer-events-auto"
                  variant="ghost"
                  iconLeft={<IconClose className="!size-6" />}
                />
              </div>
              <div className="px-3 pb-16">{renderMenuItems()}</div>
            </div>
          </Dialog.Body>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
